import { posNegGradientLine } from 'utils/components/TimeSeriesChart/posNegGradientLine';
import { curveStepAfter } from 'd3-shape';
import { AnySeries, seriesSvgMulti } from '@d3fc/d3fc-series';
import gridlines from 'utils/components/TimeSeriesChart/gridlines';
import { extentLinear } from '@d3fc/d3fc-extent';
import { PortfolioPlDataItem } from 'widgets/portfolioPl/portfolioPlData';
import { withOppositeAccessor } from 'utils/d3fc/accessors';
import { line } from 'utils/components/TimeSeriesChart/line';
import { getInterpolatedValue } from 'utils/interpolation';
import { PortfolioPlWidgetConfigState } from 'widgets/portfolioPl/store/config/PortfolioPlWidgetConfigState';
import classnames from 'classnames';

const yTicks = 6;

const realizedLineSeries = posNegGradientLine('realizedPnl').map((lineSeries) =>
    lineSeries.curve(curveStepAfter)
);

const totalSeries = line((d: PortfolioPlDataItem) => d.totalPnl, 'total-line');
const unrealizedSeries = line(
    (d: PortfolioPlDataItem) => d.unrealizedPnl,
    'unrealized-line'
);
const indexSeries = line(
    (d: PortfolioPlDataItem) => getInterpolatedValue(d.indexPnl),
    'index-line'
);

export const getChartSeries: (
    timeSeries: PortfolioPlDataItem[],
    chartWidth: number
) => AnySeries = (_timeSeries, _chartWidth) => {
    return seriesSvgMulti().series([
        gridlines(yTicks, true),
        ...realizedLineSeries,
        unrealizedSeries,
        indexSeries,
        totalSeries,
    ]);
};

export function computeYDomain(
    config: PortfolioPlWidgetConfigState,
    data: PortfolioPlDataItem[]
): [number, number] {
    const { hideRealizedPnl, hideUnrealizedPnl, hideTotalPnl, hideIndexPnl } =
        getHiddenSeries(config);

    const accessors = [];

    !hideRealizedPnl &&
        accessors.push(
            ...withOppositeAccessor((d: PortfolioPlDataItem) => d.realizedPnl)
        );

    !hideUnrealizedPnl &&
        accessors.push(
            ...withOppositeAccessor((d: PortfolioPlDataItem) => d.unrealizedPnl)
        );

    !hideTotalPnl &&
        accessors.push(
            ...withOppositeAccessor((d: PortfolioPlDataItem) => d.totalPnl)
        );

    if (!hideIndexPnl) {
        accessors.push(
            ...withOppositeAccessor((d: PortfolioPlDataItem) =>
                getInterpolatedValue(d.indexPnl)
            )
        );
    }
    return data.length > 0
        ? extentLinear().accessors(accessors).pad([0.1, 0.1])(data)
        : [0, 1];
}

export function getHideSeriesClasses(config: PortfolioPlWidgetConfigState) {
    const { hideRealizedPnl, hideUnrealizedPnl, hideTotalPnl, hideIndexPnl } =
        getHiddenSeries(config);

    return classnames(
        hideRealizedPnl && 'hide-realized',
        hideUnrealizedPnl && 'hide-unrealized',
        hideTotalPnl && 'hide-total',
        hideIndexPnl && 'hide-index'
    );
}

function getHiddenSeries(config: PortfolioPlWidgetConfigState) {
    const {
        hideRealizedPnl,
        hideUnrealizedPnl,
        hideTotalPnl,
        hideIndexPnl: configHidesIndexPnl,
        index,
    } = config;

    const hideIndexPnl = configHidesIndexPnl || !index;

    return {
        hideRealizedPnl,
        hideUnrealizedPnl,
        hideTotalPnl,
        hideIndexPnl,
    };
}
